'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@morpho-org/ui-v6/utils';
import { Icon, Toggletip, ToggletipContent, ToggletipTrigger } from '@repo/ui';
import { Info } from '@repo/ui/icons';
import { type ChangeEvent, use, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import type { MasterFilterState } from './types';
import { Input } from '../Input';
import { MasterFilterContext } from './MasterFilter';
function isRangeValue(value: unknown): value is RangeValue {
  if (typeof value !== 'object' || value === null) return false;
  const keys = Object.keys(value);
  return keys.every(k => k === 'min' || k === 'max');
}
function formatWithSeparators(value: string): string {
  const stripped = value.replaceAll(',', '');
  const parts = stripped.split('.');
  parts[0] = (parts[0] ?? '').replaceAll(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
}
function numberToDisplay(n: number | undefined): string {
  if (n === undefined) return '';
  return n.toLocaleString('en-US');
}
export interface RangeValue {
  min?: number;
  max?: number;
}
export interface MasterFilterRangeProps<TState extends MasterFilterState = MasterFilterState> {
  name: Extract<keyof TState, string>;
  label: string;
  minPlaceholder?: string;
  maxPlaceholder?: string;
  unit?: string;
  tooltip?: string;
  className?: string;
  debounceMs?: number;
}
export function MasterFilterRange(t0) {
  const $ = _c(66);
  const {
    name,
    label,
    minPlaceholder: t1,
    maxPlaceholder: t2,
    unit,
    tooltip,
    className,
    debounceMs: t3
  } = t0;
  const minPlaceholder = t1 === undefined ? "Min" : t1;
  const maxPlaceholder = t2 === undefined ? "Max" : t2;
  const debounceMs = t3 === undefined ? 500 : t3;
  const context = use(MasterFilterContext);
  if (!context) {
    throw new Error("MasterFilter.Range must be used within a MasterFilter.Provider");
  }
  const {
    state,
    actions
  } = context;
  let t4;
  if ($[0] !== name || $[1] !== state) {
    t4 = isRangeValue(state[name]) ? state[name] : {};
    $[0] = name;
    $[1] = state;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const range = t4;
  let t5;
  if ($[3] !== range.min) {
    t5 = () => numberToDisplay(range.min);
    $[3] = range.min;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const [minDisplay, setMinDisplay] = useState(t5);
  let t6;
  if ($[5] !== range.max) {
    t6 = () => numberToDisplay(range.max);
    $[5] = range.max;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const [maxDisplay, setMaxDisplay] = useState(t6);
  const pendingRangeRef = useRef(range);
  const [prevRange, setPrevRange] = useState(range);
  if (prevRange.min !== range.min || prevRange.max !== range.max) {
    setPrevRange(range);
    if (prevRange.min !== range.min) {
      setMinDisplay(numberToDisplay(range.min));
    }
    if (prevRange.max !== range.max) {
      setMaxDisplay(numberToDisplay(range.max));
    }
  }
  let t7;
  let t8;
  if ($[7] !== range.max || $[8] !== range.min) {
    t7 = () => {
      pendingRangeRef.current = {
        min: range.min,
        max: range.max
      };
    };
    t8 = [range.min, range.max];
    $[7] = range.max;
    $[8] = range.min;
    $[9] = t7;
    $[10] = t8;
  } else {
    t7 = $[9];
    t8 = $[10];
  }
  useLayoutEffect(t7, t8);
  let t9;
  if ($[11] !== actions || $[12] !== name) {
    t9 = () => {
      actions.updateField(name, pendingRangeRef.current);
    };
    $[11] = actions;
    $[12] = name;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const debouncedFlush = useDebouncedCallback(t9, debounceMs ?? 0);
  let t10;
  let t11;
  if ($[14] !== debouncedFlush) {
    t10 = () => () => debouncedFlush.flush();
    t11 = [debouncedFlush];
    $[14] = debouncedFlush;
    $[15] = t10;
    $[16] = t11;
  } else {
    t10 = $[15];
    t11 = $[16];
  }
  useEffect(t10, t11);
  let T0;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  if ($[17] !== actions || $[18] !== className || $[19] !== debounceMs || $[20] !== debouncedFlush || $[21] !== label || $[22] !== maxDisplay || $[23] !== maxPlaceholder || $[24] !== minDisplay || $[25] !== minPlaceholder || $[26] !== name || $[27] !== tooltip || $[28] !== unit) {
    const updateRange = newRange => {
      pendingRangeRef.current = newRange;
      if (debounceMs) {
        debouncedFlush();
      } else {
        actions.updateField(name, newRange);
      }
    };
    const clearField = field => {
      const newRange_0 = {
        ...pendingRangeRef.current,
        [field]: undefined
      };
      pendingRangeRef.current = newRange_0;
      if (debounceMs) {
        debouncedFlush.cancel();
      }
      actions.updateField(name, newRange_0);
    };
    const handleChange = field_0 => e => {
      const setDisplay = field_0 === "min" ? setMinDisplay : setMaxDisplay;
      const input = e.target;
      const cursorPos = input.selectionStart ?? 0;
      const prevValue = field_0 === "min" ? minDisplay : maxDisplay;
      const raw = input.value.replaceAll(/[^0-9.,-]/g, "");
      const stripped = raw.replaceAll(",", "");
      if ((stripped.match(/\./g) ?? []).length > 1) {
        return;
      }
      if (/\.\d{3,}/.test(stripped)) {
        return;
      }
      if (stripped === "" || stripped === "-" || stripped.endsWith(".")) {
        setDisplay(raw);
        if (stripped === "") {
          clearField(field_0);
        }
        requestAnimationFrame(() => {
          input.setSelectionRange(cursorPos, cursorPos);
        });
        return;
      }
      const num = Number(stripped);
      if (!isNaN(num)) {
        const formatted = formatWithSeparators(stripped);
        setDisplay(formatted);
        updateRange({
          ...pendingRangeRef.current,
          [field_0]: num
        });
        const commasBefore = (prevValue.slice(0, cursorPos).match(/,/g) ?? []).length;
        const commasAfter = (formatted.slice(0, cursorPos).match(/,/g) ?? []).length;
        const adjustedCursor = cursorPos + (commasAfter - commasBefore);
        requestAnimationFrame(() => {
          input.setSelectionRange(adjustedCursor, adjustedCursor);
        });
      }
    };
    if ($[40] !== className) {
      t20 = cn("gap-2xs py-2xs flex flex-col", className);
      $[40] = className;
      $[41] = t20;
    } else {
      t20 = $[41];
    }
    let t22;
    if ($[42] !== label) {
      t22 = <span className="text-2xs text-foreground">{label}</span>;
      $[42] = label;
      $[43] = t22;
    } else {
      t22 = $[43];
    }
    let t23;
    if ($[44] !== tooltip) {
      t23 = tooltip && <Toggletip><ToggletipTrigger asChild={true}><span className="cursor-help" role="button" tabIndex={0} aria-label="More information"><Icon icon={<Info />} className="text-muted-foreground hover:text-secondary-foreground" /></span></ToggletipTrigger><ToggletipContent className="z-60">{tooltip}</ToggletipContent></Toggletip>;
      $[44] = tooltip;
      $[45] = t23;
    } else {
      t23 = $[45];
    }
    if ($[46] !== t22 || $[47] !== t23) {
      t21 = <div className="gap-2xs flex items-center">{t22}{t23}</div>;
      $[46] = t22;
      $[47] = t23;
      $[48] = t21;
    } else {
      t21 = $[48];
    }
    t18 = "gap-2xs flex items-center";
    t19 = <Input type="text" inputMode="decimal" placeholder={minPlaceholder} data-testid={`range-${name}-min`} value={minDisplay} onChange={handleChange("min")} suffix={unit} wrapperClassName="flex-1" />;
    T0 = Input;
    t12 = "text";
    t13 = "decimal";
    t14 = maxPlaceholder;
    t15 = `range-${name}-max`;
    t16 = maxDisplay;
    t17 = handleChange("max");
    $[17] = actions;
    $[18] = className;
    $[19] = debounceMs;
    $[20] = debouncedFlush;
    $[21] = label;
    $[22] = maxDisplay;
    $[23] = maxPlaceholder;
    $[24] = minDisplay;
    $[25] = minPlaceholder;
    $[26] = name;
    $[27] = tooltip;
    $[28] = unit;
    $[29] = T0;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
    $[33] = t15;
    $[34] = t16;
    $[35] = t17;
    $[36] = t18;
    $[37] = t19;
    $[38] = t20;
    $[39] = t21;
  } else {
    T0 = $[29];
    t12 = $[30];
    t13 = $[31];
    t14 = $[32];
    t15 = $[33];
    t16 = $[34];
    t17 = $[35];
    t18 = $[36];
    t19 = $[37];
    t20 = $[38];
    t21 = $[39];
  }
  let t22;
  if ($[49] !== T0 || $[50] !== t12 || $[51] !== t13 || $[52] !== t14 || $[53] !== t15 || $[54] !== t16 || $[55] !== t17 || $[56] !== unit) {
    t22 = <T0 type={t12} inputMode={t13} placeholder={t14} data-testid={t15} value={t16} onChange={t17} suffix={unit} wrapperClassName="flex-1" />;
    $[49] = T0;
    $[50] = t12;
    $[51] = t13;
    $[52] = t14;
    $[53] = t15;
    $[54] = t16;
    $[55] = t17;
    $[56] = unit;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== t18 || $[59] !== t19 || $[60] !== t22) {
    t23 = <div className={t18}>{t19}{t22}</div>;
    $[58] = t18;
    $[59] = t19;
    $[60] = t22;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  let t24;
  if ($[62] !== t20 || $[63] !== t21 || $[64] !== t23) {
    t24 = <div className={t20}>{t21}{t23}</div>;
    $[62] = t20;
    $[63] = t21;
    $[64] = t23;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  return t24;
}
MasterFilterRange.displayName = 'MasterFilterRange';